/**
 * Fluent Builder API for workflow invocation.
 *
 * Usage:
 *   const runtime = createRuntime({ adapter, registries });
 *   const result = await runtime
 *     .workflow("feature-implement")
 *     .handoff(handoffs.featureImplementationRequest({ ... }))
 *     .maxFollowUps(3)
 *     .onGate(async () => true)
 *     .run();
 */

import {
  runWorkflow,
  type HandoffInput,
  type WorkflowInvocation,
  type WorkflowRegistries,
  type WorkflowResult,
  type StepCompleteEvent,
  type StepResult,
  type DelegateStep,
} from "./workflow-runner.js";
import type { SdkAdapter, TaskContext } from "./task-runner.js";

// ---------------------------------------------------------------------------
// Runtime factory
// ---------------------------------------------------------------------------

export interface RuntimeConfig {
  adapter: SdkAdapter;
  registries?: WorkflowRegistries;
}

export interface Runtime {
  workflow(workflowId: string): WorkflowBuilder;
}

export function createRuntime(config: RuntimeConfig): Runtime {
  return {
    workflow(workflowId: string): WorkflowBuilder {
      return new WorkflowBuilder(config.adapter, workflowId, config.registries);
    },
  };
}

// ---------------------------------------------------------------------------
// WorkflowBuilder
// ---------------------------------------------------------------------------

export class WorkflowBuilder {
  private adapter: SdkAdapter;
  private invocation: WorkflowInvocation;
  private registries: WorkflowRegistries | undefined;

  constructor(
    adapter: SdkAdapter,
    workflowId: string,
    registries?: WorkflowRegistries,
  ) {
    this.adapter = adapter;
    this.invocation = { workflow: workflowId };
    this.registries = registries;
  }

  /** Set a typed handoff envelope as input. */
  handoff<T extends string>(input: HandoffInput<T>): this {
    this.invocation = { ...this.invocation, handoff: input };
    return this;
  }

  /** Set a simple string request (convenience shorthand). */
  request(userRequest: string): this {
    this.invocation = { ...this.invocation, user_request: userRequest };
    return this;
  }

  maxFollowUps(n: number): this {
    this.invocation = {
      ...this.invocation,
      runtime: { ...this.invocation.runtime, maxFollowUps: n },
    };
    return this;
  }

  maxRetries(n: number): this {
    this.invocation = {
      ...this.invocation,
      runtime: { ...this.invocation.runtime, maxRetries: n },
    };
    return this;
  }

  onStepComplete(
    cb: (event: StepCompleteEvent) => void,
  ): this {
    this.invocation = {
      ...this.invocation,
      hooks: { ...this.invocation.hooks, onStepComplete: cb },
    };
    return this;
  }

  onGate(
    cb: (
      gateKind: string,
      description: string,
      stepResults: StepResult[],
    ) => Promise<boolean>,
  ): this {
    this.invocation = {
      ...this.invocation,
      hooks: { ...this.invocation.hooks, onGate: cb },
    };
    return this;
  }

  onOptionalStep(
    cb: (step: DelegateStep, context: TaskContext) => Promise<boolean>,
  ): this {
    this.invocation = {
      ...this.invocation,
      hooks: { ...this.invocation.hooks, onOptionalStep: cb },
    };
    return this;
  }

  /** Set progress output sink for real-time execution visibility. */
  progressOutput(sink: { write(chunk: string): unknown }): this {
    this.invocation = { ...this.invocation, progressOutput: sink };
    return this;
  }

  async run(): Promise<WorkflowResult> {
    return runWorkflow(this.adapter, this.invocation, this.registries);
  }
}
